import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Calendar, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Blog = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog?published_only=true`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const getTitle = (post) => (i18n.language === 'tr' ? post.title_tr : post.title_en);
  const getExcerpt = (post) => (i18n.language === 'tr' ? post.excerpt_tr : post.excerpt_en);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div data-testid="blog-page" className="overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 md:pb-32 bg-[#0F1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-6">
              {t('blog.title')}
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-none mb-8">
              {t('blog.subtitle')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-24 md:py-32 bg-[#0A1423]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#1B2B4B]/50 border border-[#C9A84C]/10 animate-pulse">
                  <div className="aspect-[16/9] bg-[#1B2B4B]" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-[#1B2B4B] rounded w-1/3" />
                    <div className="h-6 bg-[#1B2B4B] rounded" />
                    <div className="h-4 bg-[#1B2B4B] rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="text-white/50 text-lg">{t('blog.noPost')}</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`blog-card-${post.id}`}
                >
                  <Link
                    to={`/blog/${post.id}`}
                    className="group block bg-[#1B2B4B]/50 border border-[#C9A84C]/10 hover:border-[#C9A84C]/40 transition-all duration-500 overflow-hidden"
                  >
                    {post.image_url ? (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={getTitle(post)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-gradient-to-br from-[#C9A84C]/20 to-transparent flex items-center justify-center">
                        <span className="font-heading text-4xl text-[#C9A84C]/30">BEU</span>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center gap-2 text-white/40 text-sm">
                          <Calendar size={14} />
                          {formatDate(post.created_at)}
                        </div>
                        <div className="flex items-center gap-2 text-[#C9A84C] text-sm">
                          <Tag size={14} />
                          {post.category}
                        </div>
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-white mb-3 group-hover:text-[#C9A84C] transition-colors">
                        {getTitle(post)}
                      </h3>
                      <p className="text-white/50 text-sm leading-relaxed mb-4">
                        {getExcerpt(post)}
                      </p>
                      <span className="inline-flex items-center gap-2 text-[#C9A84C] text-sm font-semibold group-hover:gap-3 transition-all">
                        {t('blog.readMore')}
                        <ArrowRight size={16} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
